import React, { PropsWithChildren, ReactElement, useCallback, useEffect, useState } from "react";
import useSWR from "swr";
import {
  Medication,
  MedicationProfileSearchResponse,
  PatientMedicationProfile,
  PatientMedicationProfileCreateParams,
  PatientMedicationProfileItem,
  PatientMedicationProfileWithDetails,
} from "../../../messages/MedicationProfile";
import { PatientId, PatientMedicationProfileId } from "../../../messages/ObjectIds";
import { PatientDocumentContentItem } from "../../../messages/PatientDocuments";
import { VitalExtraDetails } from "../../../messages/Vital";
import { STRINGS } from "../../../strings";
import { translate } from "../../../translate";
import { HashMap } from "../../../utils/HashMap";
import useDebounce from "../../../utils/useDebounce";
import { AppCard, AppCardContent, AppCardHeader, AppCardTitle } from "../../Generic/AppCard";
import { If } from "../../Generic/If";
import { isDefined, IsDefined } from "../../Generic/IsDefined";
import PromptBottomSheet from "../../PromptBottomSheet/PromptBottomSheet";
import WarningCard from "../../WarningCard/WarningCard";
import AddNewMedicationButton from "./AddNewMedicationButton/AddNewMedicationButton";
import MedicationCard from "./MedicationCard/MedicationCard";
import { findEqualMedication, isEqualMedicationsLists } from "./MedicationProfileUtils";
import SearchMedicationCard from "./SearchMedicationCard/SearchMedicationCard";
import classNames from "classnames";

interface Props {
  item: PatientDocumentContentItem;
  isDisabled: boolean;
  required: boolean;
  showErrors: boolean;
  searchMedications(value: string): Promise<MedicationProfileSearchResponse>;
  getPatientMedicationProfile: () => Promise<PatientMedicationProfile>;
  onCreateMedication(
    params: PatientMedicationProfileCreateParams
  ): Promise<PatientMedicationProfileItem>;
  onUpdatedMedication(medication: PatientMedicationProfileWithDetails): Promise<void>;
  onDeleteMedication(medication: PatientMedicationProfileWithDetails): Promise<void>;
  onInsertAnswer: (
    id: number,
    answer: PatientMedicationProfileWithDetails[],
    vitalExtraDetails?: VitalExtraDetails
  ) => Promise<void>;
}

function MedicationProfileDocumentSection({
  item,
  isDisabled,
  required,
  showErrors,
  searchMedications,
  getPatientMedicationProfile,
  onCreateMedication,
  onUpdatedMedication,
  onDeleteMedication,
  onInsertAnswer,
}: PropsWithChildren<Props>): ReactElement {
  const [isSearchOpen, setSearch] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const [medications, setMedications] = useState<Medication[] | null>(null);
  const [patientProfileMedications, setPatientProfileMedications] = useState<
    PatientMedicationProfileWithDetails[] | null
  >(null);
  const [patientMedications, setPatientMedications] = useState<
    PatientMedicationProfileWithDetails[]
  >([]);
  const [editableMedications, setEditableMedications] = useState(
    HashMap.empty<PatientMedicationProfileId, boolean>()
  );
  const [syncMedications, setSyncMedications] = useState(
    HashMap.empty<PatientMedicationProfileId, boolean>()
  );
  const [draftedMedications, setDraftedMedications] = useState(
    HashMap.empty<PatientMedicationProfileId, boolean>()
  );
  const [medicationForDeletion, setMedicationForDeletion] =
    useState<PatientMedicationProfileWithDetails>();
  const [areDifferentMedications, setDifferentMedications] = useState<boolean>(false);

  const [hasError, setError] = useState(false);
  const debounceSearchValue = useDebounce(searchValue, 500);

  const { mutate } = useSWR("medicationsProfile", getPatientMedicationProfile, {
    onSuccess: (data) => {
      setPatientProfileMedications(data.medications);
      const nonDrafts = patientMedications.filter((m) => !isDraft(m));
      const isEqual = isEqualMedicationsLists(nonDrafts, data.medications);
      setDifferentMedications(!isEqual);
    },
  });

  const stableSearchAndSetMedications = useCallback(() => {
    setError(false);
    searchMedications(debounceSearchValue).then((res) => setMedications(res.medications));
  }, [debounceSearchValue, searchMedications]);

  const scrollToButton = React.useCallback(() => {
    const bodyDOM = document.querySelector(".ScheduledVisitDocScreen__Scaffold");
    const buttonWrapperDOM = document.getElementById("medication-button-wrapper");
    const headerDOM = document.querySelector<HTMLElement>(".ScreenSkeleton__header");

    if (bodyDOM !== null && buttonWrapperDOM !== null && headerDOM !== null) {
      bodyDOM.scroll({
        top: buttonWrapperDOM.offsetTop - headerDOM.offsetTop,
        behavior: "smooth",
      });
    }
  }, []);

  const hasAnswerMedicationInProfile = React.useCallback(
    (medicationProfileAnswer: PatientMedicationProfileWithDetails) => {
      if (!isDefined(patientProfileMedications) || patientProfileMedications.length === 0) {
        return false;
      }

      const foundMedication = findEqualMedication(
        medicationProfileAnswer,
        patientProfileMedications
      );
      return foundMedication !== null;
    },
    [patientProfileMedications]
  );

  const setMedicationProfileAnswer = React.useCallback(
    async (medicationProfileAnswer: PatientMedicationProfileWithDetails[]) => {
      await onInsertAnswer(item.id, medicationProfileAnswer);
    },
    [item, onInsertAnswer]
  );

  const isProfileSync = React.useCallback(
    (medication: PatientMedicationProfileWithDetails) => {
      if (!syncMedications.has(medication.id) && isDefined(patientProfileMedications)) {
        syncMedications.set(medication.id, hasAnswerMedicationInProfile(medication));
      }
      return syncMedications.get(medication.id) ?? true;
    },
    [hasAnswerMedicationInProfile, patientProfileMedications, syncMedications]
  );

  const updateMedicationSync = React.useCallback(
    (patientMedicationProfileId: PatientMedicationProfileId, newValue: boolean) => {
      setSyncMedications(syncMedications.set(patientMedicationProfileId, newValue));
    },
    [syncMedications]
  );

  const updateEditMedication = React.useCallback(
    (patientMedicationProfileId: PatientMedicationProfileId, newValue: boolean) => {
      if (isDisabled) {
        return;
      }
      setEditableMedications(editableMedications.set(patientMedicationProfileId, newValue));
    },
    [editableMedications, isDisabled]
  );

  const isDraft = React.useCallback(
    (medication: PatientMedicationProfileWithDetails) => {
      return draftedMedications.get(medication.id) ?? false;
    },
    [draftedMedications]
  );

  const updateDraftedMedication = React.useCallback(
    (patientMedicationProfileId: PatientMedicationProfileId, newValue: boolean) => {
      setDraftedMedications(draftedMedications.set(patientMedicationProfileId, newValue));
    },
    [draftedMedications]
  );

  const resetAnswerMedicationProfile = React.useCallback(async () => {
    if (isDefined(patientProfileMedications)) {
      await setMedicationProfileAnswer(patientProfileMedications);
      setPatientMedications(patientProfileMedications);
      setEditableMedications(HashMap.empty<PatientMedicationProfileId, boolean>());
      setSyncMedications(HashMap.empty<PatientMedicationProfileId, boolean>());
      await mutate();
    }
  }, [mutate, patientProfileMedications, setMedicationProfileAnswer]);

  const createMedicationProfileDraft = React.useCallback(
    (newMedicationProfile: Omit<PatientMedicationProfileWithDetails, "id">) => {
      const minId = patientMedications
        .filter((med) => PatientMedicationProfileId.unwrap(med.id) < 0)
        .reduce(
          (min, code) =>
            PatientMedicationProfileId.unwrap(code.id) < min
              ? PatientMedicationProfileId.unwrap(code.id)
              : min,
          0
        );

      const nextAnswerId = minId - 1;

      const created = {
        ...newMedicationProfile,
        id: PatientMedicationProfileId.wrap(nextAnswerId),
      };

      return created;
    },
    [patientMedications]
  );

  const updateMedicationProfileAnswer = React.useCallback(
    async (changedMedicationProfile: PatientMedicationProfileWithDetails) => {
      const foundIndex = patientMedications.findIndex(
        (medication) => medication.id === changedMedicationProfile.id
      );
      let changedMedications;
      if (foundIndex === -1) {
        changedMedications = [...patientMedications, changedMedicationProfile];
      } else {
        changedMedications = patientMedications.map((medication) =>
          medication.id === changedMedicationProfile.id
            ? { ...medication, ...changedMedicationProfile }
            : medication
        );
      }

      setPatientMedications(changedMedications);
      await setMedicationProfileAnswer(changedMedications);
    },
    [patientMedications, setMedicationProfileAnswer]
  );

  const removeMedicationProfileAnswer = React.useCallback(
    async (id: PatientMedicationProfileId) => {
      const filteredMedications = patientMedications.filter((medication) => medication.id !== id);
      setPatientMedications(filteredMedications);
      await setMedicationProfileAnswer(filteredMedications);
    },
    [patientMedications, setMedicationProfileAnswer]
  );

  const handleChooseMedication = React.useCallback(
    async (medication: Medication) => {
      try {
        const created = createMedicationProfileDraft({
          patientId: PatientId.wrap(-1),
          medicationId: medication.id,
          from: null,
          until: null,
          frequency: null,
          status: "ACTIVE",
          notes: null,
          dose: medication.dose,
          route: medication.route,
          medicationName: medication.displayName,
        });

        const newPatientMedications = [...patientMedications, created];

        setPatientMedications(newPatientMedications);

        setSearch(false);
        updateEditMedication(created.id, true);
        updateMedicationSync(created.id, true);
        updateDraftedMedication(created.id, true);

        await mutate();
      } catch (err) {
        console.log(err);
        setError(true);
      }
    },
    [
      createMedicationProfileDraft,
      mutate,
      patientMedications,
      updateDraftedMedication,
      updateEditMedication,
      updateMedicationSync,
    ]
  );

  const handleUpdatedMedication = React.useCallback(
    async (medication: PatientMedicationProfileWithDetails) => {
      const syncMedicationProfile = isProfileSync(medication);
      if (syncMedicationProfile) {
        if (PatientMedicationProfileId.unwrap(medication.id) < 0) {
          const created = await onCreateMedication({
            medicationId: medication.medicationId,
            from: medication.from,
            until: medication.until,
            frequency: medication.frequency,
            notes: medication.notes,
            dose: medication.dose,
          });

          if (isDefined(created)) {
            medication.id = created.id;
            medication.patientId = created.patientId;
            updateMedicationSync(medication.id, syncMedicationProfile);
          }
        } else {
          await onUpdatedMedication(medication);
        }
      }

      await updateMedicationProfileAnswer(medication);
      updateDraftedMedication(medication.id, false);

      await mutate();
    },
    [
      isProfileSync,
      mutate,
      onCreateMedication,
      onUpdatedMedication,
      updateDraftedMedication,
      updateMedicationProfileAnswer,
      updateMedicationSync,
    ]
  );

  const handleChangeMedication = React.useCallback(
    (medication: PatientMedicationProfileWithDetails) => {
      setPatientMedications([
        ...patientMedications.map((med) =>
          med.medicationId === medication.medicationId ? medication : med
        ),
      ]);
    },
    [patientMedications]
  );

  const handleDeleteMedication = React.useCallback(
    async (removedMedication: PatientMedicationProfileWithDetails) => {
      if (isDraft(removedMedication)) {
        setPatientMedications(
          patientMedications.filter((medication) => medication.id !== removedMedication.id)
        );
      } else {
        const syncMedicationProfile = syncMedications.get(removedMedication.id) ?? true;
        const isAnswerId = PatientMedicationProfileId.unwrap(removedMedication.id) < 0;

        if (syncMedicationProfile && !isAnswerId) {
          await onDeleteMedication(removedMedication);
        }

        await removeMedicationProfileAnswer(removedMedication.id);

        await mutate();
      }
    },
    [
      isDraft,
      mutate,
      onDeleteMedication,
      patientMedications,
      removeMedicationProfileAnswer,
      syncMedications,
    ]
  );

  const handleAfterPromptDelete = React.useCallback(
    async (value: any) => {
      if (value === true && medicationForDeletion !== undefined) {
        await handleDeleteMedication(medicationForDeletion);
      }

      setMedicationForDeletion(undefined);
    },
    [handleDeleteMedication, medicationForDeletion]
  );

  const handleToggleProfileSync = React.useCallback(
    (medication: PatientMedicationProfileWithDetails) => {
      const isSync = syncMedications.get(medication.id) ?? hasAnswerMedicationInProfile(medication);
      updateMedicationSync(medication.id, !isSync);
    },
    [hasAnswerMedicationInProfile, syncMedications, updateMedicationSync]
  );

  const isEditable = React.useCallback(
    (medication: PatientMedicationProfileWithDetails) => {
      return editableMedications.get(medication.id) ?? false;
    },
    [editableMedications]
  );

  useEffect(() => {
    if (item.columns === null || item.columns === undefined) {
      throw new Error(`columns should not be empty on MedicationProfile`);
    }

    const patientMedicationsList: PatientMedicationProfileWithDetails[] = [];
    const medItems = item.columns[0];

    medItems.forEach((medColumn: PatientDocumentContentItem) => {
      const { patientMedicationDetails } = medColumn;
      if (patientMedicationDetails !== undefined) {
        patientMedicationsList.push(patientMedicationDetails);
      }
    });

    setPatientMedications(patientMedicationsList);
  }, [item.columns]);

  useEffect(() => {
    if (isDefined(patientProfileMedications)) {
      const nonDrafts = patientMedications.filter((m) => !isDraft(m));
      const isEqual = isEqualMedicationsLists(nonDrafts, patientProfileMedications);
      setDifferentMedications(!isEqual);
    }
  }, [isDraft, patientMedications, patientProfileMedications]);

  useEffect(() => {
    if (isSearchOpen) {
      scrollToButton();
    }

    if (debounceSearchValue !== "") {
      stableSearchAndSetMedications();
    }
  }, [debounceSearchValue, isSearchOpen, stableSearchAndSetMedications, scrollToButton]);

  return (
    <AppCard
      className={classNames("AppCardMedicationProfileDocumentSection", {
        __invalid: required && patientMedications.length === 0 && showErrors,
      })}
    >
      <AppCardHeader>
        <AppCardTitle>
          Medications {required && <span className="__badge">{translate(STRINGS.REQUIRED)}</span>}
        </AppCardTitle>
      </AppCardHeader>
      <AppCardContent>
        <div className="MedicationProfileDocumentSection">
          <If condition={areDifferentMedications}>
            <WarningCard
              message={translate(STRINGS.INFORMATION_NOT_MATCH_PROFILE)}
              actionTitle={translate(STRINGS.UPDATE_INFORMATION)}
              onAction={resetAnswerMedicationProfile}
              readonly={isDisabled}
            />
          </If>

          <If condition={!isDisabled}>
            <div id="medication-button-wrapper">
              <AddNewMedicationButton onClick={() => setSearch(true)} />
            </div>
          </If>

          <SearchMedicationCard
            value={searchValue}
            medications={medications}
            isOpen={isSearchOpen}
            onChange={setSearchValue}
            hasError={hasError}
            onRequestClose={() => setSearch(false)}
            onMedicationChosen={handleChooseMedication}
          />
          <If condition={patientMedications.length !== 0}>
            {patientMedications.map((medication, index) => (
              <MedicationCard
                key={index}
                medication={medication}
                isDisabled={isDisabled}
                isEditable={isEditable(medication)}
                isProfileSync={isProfileSync(medication)}
                onChange={handleChangeMedication}
                onRequestSave={() => handleUpdatedMedication(medication)}
                onRequestToggleEdit={(editMode: boolean) =>
                  updateEditMedication(medication.id, editMode)
                }
                onRequestDelete={() => setMedicationForDeletion(medication)}
                onClickProfileSync={() => handleToggleProfileSync(medication)}
              />
            ))}
          </If>

          <If condition={!isDisabled && patientMedications.length > 0}>
            <div id="medication-button-wrapper">
              <AddNewMedicationButton onClick={() => setSearch(true)} />
            </div>
          </If>

          <IsDefined value={medicationForDeletion}>
            {(medication) => (
              <PromptBottomSheet
                title={translate(STRINGS.WARNING)}
                isOpen={true}
                options={[
                  { value: false, label: translate(STRINGS.NO) },
                  { value: true, label: translate(STRINGS.YES) },
                ]}
                onChoose={handleAfterPromptDelete}
                onRequestClose={() => setMedicationForDeletion(undefined)}
                message={
                  [
                    translate(STRINGS.ARE_YOU_SURE_YOU_WANT_TO_DELETE),
                    medication.medicationName,
                  ].join(" ") + "?"
                }
              />
            )}
          </IsDefined>
        </div>
      </AppCardContent>
    </AppCard>
  );
}

export default MedicationProfileDocumentSection;
